/* Domovoi · push notifications on this phone (D2.5). App only; on the web
   every function here is a quiet no-op.

   We never ask on first open. The shell asks after a moment worth hearing
   about (a first match), and Account → Notifications has a button. Turning
   them on registers this phone's FCM token (register_push_token); what each
   of you hears about lives in notification_preferences; push_outbox and the
   push-send function do the sending.

   Tapping a notification opens its screen: the payload carries a product
   address ("/curated/?tab=list"), handed to the shell as a dv-open event.

   PUSH_READY says which phones can register: iPhones send straight to Apple
   (the APNs key is in the vault); Android waits for the Firebase project and
   google-services.json in a native build.

   And only where the PostHog flag `app-push` says yes: since 2026-10-28 that's
   everyone signed in (the flag needs an email on the person) except the Sam and
   Alex test account, which App Review signs in as. */
import { isNative, platform } from './platform.js';
import { supabase } from './supabase.js';
import { stored } from './session.js';
import { PUSH_READY } from './config.js';

const DEVICE_KEY = 'domovoi.device.id';
let plugin = null;

/* A Capacitor plugin is a proxy that answers every property, "then" included,
   so it must never be the value a promise resolves to: awaiting it calls
   PushNotifications.then(), which iOS rejects, and push never started. Hand it
   back wrapped. */
async function P() {
  if (!plugin) plugin = (await import('@capacitor/push-notifications')).PushNotifications;
  return { Push: plugin };
}

function deviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) { id = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2)); localStorage.setItem(DEVICE_KEY, id); }
    return id;
  } catch (e) { return 'unknown'; }
}

export function pushAvailable() { return isNative() && !!PUSH_READY[platform()]; }

/* The flag, once PostHog has it for this person. Flags load once anonymously
   and again after identify(), so a "yes" from either load counts; with no
   "yes" after 5 s, it's off. Remembered for the page once on. */
const FLAG = 'app-push';
let flagged = null;
function flagOn() {
  if (flagged) return flagged;
  flagged = new Promise((resolve) => {
    const ph = window.posthog;
    if (!ph || typeof ph.onFeatureFlags !== 'function') { flagged = null; resolve(false); return; }
    let settled = false;
    const settle = (v) => { if (!settled) { settled = true; if (!v) flagged = null; resolve(v); } };
    setTimeout(() => settle(!!ph.isFeatureEnabled(FLAG)), 5000);
    ph.onFeatureFlags(() => { if (ph.isFeatureEnabled(FLAG)) settle(true); });
  });
  return flagged;
}
async function allowed() { return pushAvailable() && (await flagOn()); }

/* 'unavailable' (web, or not set up yet) | 'prompt' | 'granted' | 'denied' */
export async function pushState() {
  if (!(await allowed())) return 'unavailable';
  try {
    const { receive } = await (await P()).Push.checkPermissions();
    return receive === 'granted' ? 'granted' : receive === 'denied' ? 'denied' : 'prompt';
  } catch (e) { return 'unavailable'; }
}

/* Ask (if we haven't), then register. Resolves to the new state. */
export async function enablePush(where) {
  if (!(await allowed())) return 'unavailable';
  const { Push } = await P();
  let { receive } = await Push.checkPermissions();
  if (receive !== 'granted' && receive !== 'denied') ({ receive } = await Push.requestPermissions());
  if (window.DVTrack) window.DVTrack('push_permission', { product: 'account', result: receive, where: where || null });
  if (receive !== 'granted') return receive === 'denied' ? 'denied' : 'prompt';
  attach(Push);
  await Push.register();
  // Save the time zone the quiet hours and reminders should use.
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const s = stored();
    if (tz && s) await supabase().from('notification_preferences').upsert({ user_id: s.user.id, time_zone: tz }, { onConflict: 'user_id' });
  } catch (e) {}
  return 'granted';
}

/* The listeners that save this phone's token and open tapped notifications.
   Every page that can call register() needs them: turning notifications on
   from the account page (its own page in the app) used to register with
   nobody listening, so the token was only saved on the next app start. */
let attached = false;
function attach(Push) {
  if (attached) return;
  attached = true;
  Push.addListener('registration', async ({ value }) => {
    if (!stored()) return;
    try {
      await supabase().rpc('register_push_token', { p_token: value, p_platform: platform(), p_device_id: deviceId(),
        p_app_version: (window.DVShell && window.DVShell.version) || null });
    } catch (e) { if (window.DVTrackError) window.DVTrackError(e, { where: 'push_register' }); }
  });
  Push.addListener('registrationError', (e) => { if (window.DVTrackError) window.DVTrackError(new Error(e && e.error || 'push registration'), { where: 'push_register' }); });
  Push.addListener('pushNotificationActionPerformed', ({ notification }) => {
    const url = notification && notification.data && notification.data.url;
    if (window.DVTrack) window.DVTrack('push_opened', { product: 'account', kind: (notification && notification.data && notification.data.kind) || null });
    if (url && /^\/[a-z]/.test(url)) window.dispatchEvent(new CustomEvent('dv-open', { detail: { path: url } }));
  });
}

/* At app start: listeners, and a fresh token if notifications are already on. */
export async function startPush() {
  if (!(await allowed())) return;
  const { Push } = await P();
  attach(Push);
  if ((await Push.checkPermissions()).receive === 'granted') await Push.register();
}

/* Sign-out: this phone stops getting the last account's notifications. */
export async function forgetPushDevice() {
  if (!pushAvailable()) return;
  try { await supabase().rpc('unregister_push_token', { p_device_id: deviceId() }); } catch (e) {}
}
